import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApiCallService } from "../../../../api/service/api-call.service";
import * as Constants from "../../../../constant/Constants";
import { IConfirmDialogData } from "src/app/components/dialog/dialog.data";
import { ConfirmComponent } from "src/app/components/dialog/confirm/confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-dp-quote",
  templateUrl: "./quote.component.html",
  styleUrls: ["./quote.component.css"],
})
export class DpQuoteComponent implements OnInit {
  token: string = "placeholder-value";
  loading: boolean = false;
  url: string = environment.baseUrl.replace("/motor","") + "/dp-chat" + Constants.dpQuoteURL;

  typeOfHouseOptions: string[] = ["Apartment/Flat", "Bungalow", "Town house/Villa","Maisonette"]

  liabilityTypeOptions: string[] = ["Occupier", "Owner"];

  genericDialogData: IConfirmDialogData = {
    title: "",
    content: "",
    cancelButtonText: "",
    confirmButtonText: "",
  };

  dpForm: FormGroup = this._formBuilder.group({
    buildingEnabled: [false],
    buildingAmount: [""],
    buildingType: [""],

    contentsEnabled: [false],
    electronics: [""],
    mobilePhones: [""],
    laptops: [""],
    houseFurnishings: [""],
    kitchenAppliances: [""],
    cctvSolar: [""],
    clothing: [""],
    othersContents: [""],

    staffEnabled: [false],
    staffHousehelp: [0],
    staffDriver: [0],
    staffCaretaker: [0],
    staffGardener: [0],
    staffWatchman: [0],

    liabilityEnabled: [false],
    liabilityType: [""],
    liability: [""],
  });

  constructor(
    private route: ActivatedRoute,
    private apiCallService: ApiCallService,
    private _formBuilder: FormBuilder,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params["token"] && params["token"] !== "undefined") {
        this.token = params["token"];
        this.loadPrefill();
      }
    });

    const amountFields = [
      "buildingAmount", "electronics", "mobilePhones", "laptops",
      "houseFurnishings", "kitchenAppliances", "cctvSolar", "clothing",
      "othersContents", "liability",
    ];

    amountFields.forEach((field) => {
      this.dpForm.get(field)?.valueChanges.subscribe((value) => {
        if (value) {
          const raw = value.replace(/[^0-9]/g, "");
          if (raw) {
            const formatted = Number(raw).toLocaleString("en-KE");
            if (value !== formatted) {
              this.dpForm.get(field)?.setValue(formatted, { emitEvent: false });
            }
          }
        }
      });
    });
  }

  get buildingEnabled(): boolean {
    return this.dpForm.get("buildingEnabled")?.value ?? false;
  }

  get contentsEnabled(): boolean {
    return this.dpForm.get("contentsEnabled")?.value ?? false;
  }

  get staffEnabled(): boolean {
    return this.dpForm.get("staffEnabled")?.value ?? false;
  }

  get liabilityEnabled(): boolean {
    return this.dpForm.get("liabilityEnabled")?.value ?? false;
  }

  get atLeastOneSelected(): boolean {
    return this.buildingEnabled || this.contentsEnabled || this.staffEnabled || this.liabilityEnabled;
  }

  get hasRequiredContentsItems(): boolean {
    return (
      this.parseAmount(this.dpForm.get('houseFurnishings')?.value) > 0 ||
      this.parseAmount(this.dpForm.get('kitchenAppliances')?.value) > 0 ||
      this.parseAmount(this.dpForm.get('clothing')?.value) > 0
    );
  }

  get buildingValid(): boolean {
    const hasType = !!this.dpForm.get('buildingType')?.value;
    const hasAmount = this.parseAmount(this.dpForm.get('buildingAmount')?.value) > 0;
    return hasType && hasAmount;
  }

  get canProceed(): boolean {
    return this.buildingValid || this.hasRequiredContentsItems;
  }

  get buildingDisplayTotal(): string {
    return this.dpForm.get("buildingAmount")?.value || "0";
  }

  get contentsDisplayTotal(): string {
    const total =
      this.parseAmount(this.dpForm.get("electronics")?.value) +
      this.parseAmount(this.dpForm.get("mobilePhones")?.value) +
      this.parseAmount(this.dpForm.get("laptops")?.value) +
      this.parseAmount(this.dpForm.get("houseFurnishings")?.value) +
      this.parseAmount(this.dpForm.get("kitchenAppliances")?.value) +
      this.parseAmount(this.dpForm.get("cctvSolar")?.value) +
      this.parseAmount(this.dpForm.get("clothing")?.value) +
      this.parseAmount(this.dpForm.get("othersContents")?.value);
    return total > 0 ? total.toLocaleString("en-KE") : "0";
  }

  get staffDisplayTotal(): number {
    return (
      (this.dpForm.get("staffHousehelp")?.value || 0) +
      (this.dpForm.get("staffDriver")?.value || 0) +
      (this.dpForm.get("staffCaretaker")?.value || 0) +
      (this.dpForm.get("staffGardener")?.value || 0) +
      (this.dpForm.get("staffWatchman")?.value || 0)
    );
  }

  get liabilityDisplayTotal(): string {
    return this.dpForm.get("liability")?.value || "0";
  }

  toggle(field: string): void {
    const current = this.dpForm.get(field)?.value;
    this.dpForm.patchValue({ [field]: !current });
    if (field === 'buildingEnabled' && current) {
      this.dpForm.patchValue({ liabilityEnabled: false });
    }
  }

  parseAmount(value: string | null | undefined): number {
    if (!value) return 0;
    return parseInt(value.replace(/[^0-9]/g, ""), 10) || 0;
  }

  increment(field: string): void {
    const current = this.dpForm.get(field)?.value || 0;
    this.dpForm.get(field)?.setValue(current + 1);
  }

  decrement(field: string): void {
    const current = this.dpForm.get(field)?.value || 0;
    if (current > 0) {
      this.dpForm.get(field)?.setValue(current - 1);
    }
  }

  async loadPrefill(): Promise<void> {
    try {
      const response = await this.apiCallService.getRequest(`${this.url}?token=${this.token}`);
      if (response && response.error === false && response.prefill) {
        const p = response.prefill;
        const fmt = (v: any) => (v && Number(v) > 0 ? Number(v).toLocaleString("en-KE") : "");
        this.dpForm.patchValue({
          buildingEnabled:   p.buildingEnabled  || false,
          buildingAmount:    fmt(p.buildingAmount),
          buildingType:      p.buildingType     || "",
          contentsEnabled:   p.contentsEnabled  || false,
          electronics:       fmt(p.electronics),
          mobilePhones:      fmt(p.mobilePhones),
          laptops:           fmt(p.laptops),
          houseFurnishings:  fmt(p.houseFurnishings),
          kitchenAppliances: fmt(p.kitchenAppliances),
          cctvSolar:         fmt(p.cctvSolar),
          clothing:          fmt(p.clothing),
          othersContents:    fmt(p.othersContents),
          staffEnabled:      p.staffEnabled     || false,
          staffHousehelp:    p.staffHousehelp   || 0,
          staffDriver:       p.staffDriver      || 0,
          staffCaretaker:    p.staffCaretaker   || 0,
          staffGardener:     p.staffGardener    || 0,
          staffWatchman:     p.staffWatchman    || 0,
          liabilityEnabled:  p.liabilityEnabled || false,
          liabilityType:     p.liabilityType    || "",
          liability:         fmt(p.liability),
        });
      }
    } catch (error) {
      console.error("Failed to load prefill data", error);
    }
  }

  submitForm(): void {
    this.initDialogData(
      "Confirm submission",
      "Please proceed only if you have verified that the details are correct",
      "Back",
      "Proceed"
    );
    this.showGenericPopup(this.postDataToWhatsapp);
  }

  postDataToWhatsapp = async () => {
    this.loading = true;
    const f = this.dpForm.value;

    const req = {
      buildingEnabled: f.buildingEnabled,
      buildingAmount: this.parseAmount(f.buildingAmount),
      buildingType: f.buildingType,
      contentsEnabled: f.contentsEnabled,
      electronics: this.parseAmount(f.electronics),
      mobilePhones: this.parseAmount(f.mobilePhones),
      laptops: this.parseAmount(f.laptops),
      houseFurnishings: this.parseAmount(f.houseFurnishings),
      kitchenAppliances: this.parseAmount(f.kitchenAppliances),
      cctvSolar: this.parseAmount(f.cctvSolar),
      clothing: this.parseAmount(f.clothing),
      othersContents: this.parseAmount(f.othersContents),
      staffEnabled: f.staffEnabled,
      staffHousehelp: f.staffHousehelp,
      staffDriver: f.staffDriver,
      staffCaretaker: f.staffCaretaker,
      staffGardener: f.staffGardener,
      staffWatchman: f.staffWatchman,
      liabilityEnabled: f.liabilityEnabled,
      liabilityType: f.liabilityType,
      liability: this.parseAmount(f.liability),
      token: this.token,
      filled: true,
    };

    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Submission Failed",
          "An error occurred while saving the form details",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.initDialogData(
        "Submission Failed",
        "An unexpected error occurred",
        "Cancel",
        "TRY AGAIN"
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  };

  confirmCancel(): void {
    this.initDialogData(
      "Warning",
      "Are you sure you want to cancel the application?",
      "back",
      "Cancel application"
    );
    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () => {
    this.loading = true;
    const req = { token: this.token, filled: false };
    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Failed",
          "An error occurred while attempting to cancel your application",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      window.location.href = environment.whatsappRedirectURL;
    }
  };

  showGenericPopup(runFunction: () => void): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        runFunction();
      }
    });
  }

  initDialogData(
    title: string,
    content: string,
    cancelButtonText: string,
    confirmButtonText: string
  ): void {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  }
}
